import React, { useMemo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { handleNavigate } from "../../../hooks/handleNavigate";

import { fetchAssignmentList, setAssignment } from "../assignmentSlice";

import { formatDate, formatScore } from "../../../utils/textUtils";
import { refinePages } from "../../../utils/paginationUtils";

import { typeInfo } from "../../../constants/typeInfo";
import Pagination from "../../../components/Pagination";
import PaperAssignmentModal from "./PaperAssignmentModal";

const AssignmentTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [paperModalOpen, setPaperModalOpen] = useState(false);
  const [selectedPaperAssignment, setSelectedPaperAssignment] = useState(null);

  const { assignmentList, current_page, total_pages } = useSelector(
    (state) => state.assignment
  );
  const { student } = useSelector((state) => state.student);
  const { mode } = useSelector((state) => state.ui);
  const isMobile = mode === "mobile";

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  const handlePageClick = (page) => {
    dispatch(
      fetchAssignmentList({
        studentId: student.id,
        page,
        isFinished: false,
      })
    );
  };

  const handleLearn = async (assignment) => {
    if (assignment.type === 'paper_assignment') {
      // 지필 과제인 경우 모달 열기
      setSelectedPaperAssignment(assignment);
      setPaperModalOpen(true);
    } else if (assignment.type === 'class_content') {
      // 수업 내용(시험/과제 결과)인 경우 - 단순히 점수만 표시 (클릭 동작 없음)
      return;
    } else {
      // 일반 과제인 경우 기존 로직
      dispatch(setAssignment(assignment));
      handleNavigate({
        url: "/assignment",
        params: { id: assignment.id },
        navigate,
      });
    }
  };

  const handlePaperModalClose = () => {
    setPaperModalOpen(false);
    setSelectedPaperAssignment(null);
    // 과제 목록 새로고침
    if (student.id) {
      dispatch(fetchAssignmentList({ studentId: student.id, isFinished: false }));
    }
  };

  useEffect(() => {
    if (student.id) dispatch(fetchAssignmentList({ studentId: student.id }));
  }, [dispatch, student]);

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      <h3
        style={{
          margin: isMobile ? "10px 0" : "25px 0",
          fontSize: isMobile && "16px",
        }}
      >
        미완료 과제 목록
      </h3>
      <div
        style={{
          backgroundColor: "#FFF",
          paddingBottom: "5px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <colgroup>
            <col style={{ width: "100px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "20px" }} />
            <col />
            <col style={{ width: "200px" }} />
          </colgroup>
          <tbody>
            <tr
              style={{
                borderBottom: "1px solid #EBEBEB",
                textAlign: "left",
                color: "#737373",
                fontSize: isMobile ? "14px" : "16px",
              }}
            >
              <td style={{ textAlign: "center" }}>출제일</td>
              <td style={{ textAlign: "center" }}>마감일</td>
              <td></td>
              <td
                style={{
                  padding: isMobile ? "10px" : "14px",
                  paddingLeft: "10px",
                }}
              >
                학습지명
              </td>
              <td style={{ textAlign: "center" }}>학습하기</td>
            </tr>
            {assignmentList && assignmentList.length > 0 ? (
              assignmentList.map((assignment, index) => (
                <tr
                  key={index}
                  style={{
                    fontSize: isMobile ? "16px" : "18px",
                  }}
                >
                  <td style={{ textAlign: "center" }}>
                    {formatDate(assignment.created_at)}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {assignment.closed_at ? formatDate(assignment.closed_at) : '-'}
                  </td>
                  <td></td>
                  <td style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "10px",
                        alignItems: "center",
                      }}
                    >
                      {assignment.is_retry && (
                        <span style={{ 
                          backgroundColor: "#e8f5e8", 
                          color: "#2e7d32", 
                          padding: "4px 8px", 
                          borderRadius: "4px", 
                          fontSize: "12px",
                          marginLeft: "8px",
                          marginRight: "8px",
                          fontWeight: "bold"
                        }}>
                          복습 {assignment.retry_info?.retry_count || ''}회차
                        </span>
                      )}
                      {assignment.type === 'class_content' && assignment.class_content_data?.typeEmoji && (
                        <span style={{ marginRight: '8px' }}>
                          {assignment.class_content_data.typeEmoji}
                        </span>
                      )}
                      {assignment.title}
                      <div
                        style={{
                          borderRadius: "5px",
                          padding: "2px",
                          width: "90px",
                          fontSize: isMobile ? "13px" : "15px",
                          backgroundColor: typeInfo[assignment.type]?.badgeColor,
                          color: "#000",
                          display: "inline-block",
                          textAlign: "center",
                        }}
                      >
                        {assignment.type === 'class_content' 
                          ? assignment.class_content_data?.typeName 
                          : typeInfo[assignment.type]?.text}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "10px", textAlign: "center" }}>
                    {assignment.type === 'class_content' ? (
                      <div
                        style={{
                          backgroundColor: assignment.score >= 80 ? "#d4edda" : 
                                         assignment.score >= 60 ? "#fff3cd" : "#f8d7da",
                          color: assignment.score >= 80 ? "#155724" : 
                                assignment.score >= 60 ? "#856404" : "#721c24",
                          border: "1px solid " + (assignment.score >= 80 ? "#c3e6cb" : 
                                                assignment.score >= 60 ? "#ffeaa7" : "#f5c6cb"),
                          borderRadius: "5px",
                          padding: "5px 30px",
                          fontSize: isMobile ? "14px" : "16px",
                          fontWeight: "bold",
                        }}
                      >
                        {assignment.score !== null ? `${formatScore(assignment.score)}점` : '점수 없음'}
                      </div>
                    ) : (
                      <button
                        style={{
                          backgroundColor: "#2D2D2D",
                          color: "#FFFFFF",
                          border: "1px solid #EBEBEB",
                          borderRadius: "5px",
                          padding: "5px 30px",
                          cursor: assignment.type === 'class_content' ? "default" : "pointer",
                          fontSize: isMobile ? "14px" : "16px",
                        }}
                        onClick={() => handleLearn(assignment)}
                      >
                        {assignment.type === 'paper_assignment' && assignment.is_finished
                          ? (assignment.score !== null ? `${formatScore(assignment.score)}점` : '채점 중')
                          : assignment.type === 'paper_assignment'
                            ? '과제업로드'
                            : `${assignment.progress_rate}% 완료`}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  과제 목록이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          visiblePages={visiblePages}
          current_page={current_page}
          handlePageClick={handlePageClick}
        />
      </div>

      <PaperAssignmentModal
        isOpen={paperModalOpen}
        onClose={handlePaperModalClose}
        assignment={selectedPaperAssignment}
      />
    </div>
  );
};

export default AssignmentTable;
